/**
 * Service to manage the user's opt-in to beta experiences.
 *
 * Three-tier pre-release lifecycle:
 *   Alpha  → /reimagined/* routes (experimental, separate)
 *   Beta   → Opt-in via Profile toggle (merged into main templates, production quality)
 *   GA     → Default for everyone, old code removed
 *
 * When the `betaExperience` feature flag is enabled (local/vodka/staging),
 * the Profile page shows a toggle to opt in. Features graduating from alpha
 * to beta check `this.betaExperience.isEnabled` to swap rendering.
 *
 * Per-product toggles allow enabling beta for specific products (e.g. TA only)
 * without affecting other product areas. The global toggle acts as a master
 * switch — enabling it turns on beta for all products.
 *
 * When the flag is OFF (production), the toggle is hidden and beta features
 * are always disabled regardless of localStorage.
 */
import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type FeaturesService from 'tio-common/services/features';
import { CAPABILITY_SOURCE_RANK, type CapabilitySource } from 'tio-common/services/features';

const STORAGE_KEY = 'tio-beta-experience';
const PRODUCT_STORAGE_KEY = 'tio-beta-experience-products';

export type BetaProduct = 'ta';

export const BETA_PRODUCTS: BetaProduct[] = ['ta'];

export default class BetaExperienceService extends Service {
  @service declare features: FeaturesService;

  @tracked private _userPreference: boolean | null = null;
  @tracked private _productPreferences: Record<BetaProduct, boolean> = {
    ta: false,
  };

  constructor(owner: object) {
    // @ts-expect-error owner type
    super(owner);
    this._loadPreference();
    this._loadProductPreferences();
  }

  /** Whether the feature flag allows the beta toggle to appear */
  get flagEnabled(): boolean {
    return this.features.isEnabled('betaExperience');
  }

  /**
   * Whether beta experiences should render globally.
   * - If feature flag is OFF → always false
   * - If user has a stored preference → use it
   * - Otherwise → default to OFF (user must explicitly opt in)
   */
  get isEnabled(): boolean {
    if (!this.flagEnabled) {
      return false;
    }
    return this._userPreference ?? false;
  }

  /**
   * Whether beta is enabled for a specific product.
   * Returns true if either the global toggle OR the product-specific toggle is on.
   */
  isProductEnabled = (product: BetaProduct): boolean => {
    if (!this.flagEnabled) {
      return false;
    }
    if (this._userPreference) {
      return true;
    }
    return this._productPreferences[product] ?? false;
  };

  /** Toggle beta experience on/off globally */
  toggle = () => {
    const newValue = !this.isEnabled;
    this._userPreference = newValue;
    localStorage.setItem(STORAGE_KEY, String(newValue));
  };

  /** Explicitly set the global preference */
  setEnabled = (value: boolean) => {
    this._userPreference = value;
    localStorage.setItem(STORAGE_KEY, String(value));
  };

  /** Set per-product beta preference */
  setProductEnabled = (product: BetaProduct, value: boolean) => {
    this._productPreferences = {
      ...this._productPreferences,
      [product]: value,
    };
    localStorage.setItem(PRODUCT_STORAGE_KEY, JSON.stringify(this._productPreferences));
  };

  private _loadPreference() {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      this._userPreference = stored === 'true';
    }
  }

  /**
   * Whether the user has explicitly enabled a specific product preference
   * (ignores global toggle). Used for UI binding on disabled switches.
   */
  isProductPreferenceEnabled = (product: BetaProduct): boolean => {
    return this._productPreferences[product] ?? false;
  };

  /**
   * Expose beta opt-in as a {@link CapabilitySource} for the features service
   * (rank below demo). Answers the `betaExperience` capability with the EFFECTIVE
   * state (flag AND user opt-in) — never `flagEnabled` alone, which would force
   * beta on for everyone the moment the flag flips. Abstains on all other keys.
   */
  asCapabilitySource = (): CapabilitySource => {
    return {
      id: 'beta',
      rank: CAPABILITY_SOURCE_RANK.beta,
      resolve: (key) => (key === 'betaExperience' ? this.isEnabled : undefined),
    };
  };

  private _loadProductPreferences() {
    const stored = localStorage.getItem(PRODUCT_STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        const updated = { ...this._productPreferences };
        for (const product of BETA_PRODUCTS) {
          if (typeof parsed[product] === 'boolean') {
            updated[product] = parsed[product];
          }
        }
        this._productPreferences = updated;
      } catch {
        // Ignore invalid JSON
      }
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'beta-experience': BetaExperienceService;
  }
}
